<!-- What the bank's own statement line says. Not the same as the instrument identifiers: the bank often
     assigns its own reference rather than echoing back the one we sent, and a BANK_INITIATED movement has
     no identifier of ours at all. This is the field reconciliation actually matches on — so it renders
     beside the instrument when there is one, and in the money card when there is not. -->
<ng-template #statementReferenceField>
  @if (controls.statementReference; as reference) {
    <div class="span-12-4">
      <div class="form-box">
        <label>{{ 'BankTransaction_Details_Form_Field_BankingReference' | translateWF: 'Statement reference' }}</label>
        <input type="text" pInputText [formControl]="reference" class="w-full" [placeholder]="'BankTransaction_Reference_Placeholder' | translateWF: 'e.g. wire or check number'" />
        <small class="text-surface-500">{{ statementReferenceHint().key | translateWF: statementReferenceHint().fallback }}</small>
      </div>
    </div>
  }
</ng-template>

<div class="col">
  <!-- Whatever names the account(s) this movement touches: one bank on a standalone movement, a
       From → To pair on a transfer leg. Full width, above the columns, and only the host knows which. -->
  <ng-content select="[movementContext]" />

  <div class="grid-12 gap-[1.5rem]">
    <!-- Left column ─────────────────────────────────────────────────────────────── -->
    <div class="span-12-12-8 col">
      <!-- Instrument first, because the type decides which fields below are required: WIRE demands an
         end-to-end ID and forbids a trace number, ACH the reverse. Asking last would spring a new
         required field on someone who had already filled the form. -->
      @if (controls.instrument; as instrument) {
        <p-panel [header]="'BankTransaction_Section_Instrument' | translateWF: 'Instrument'">
          <p-card>
            <div class="grid-12">
              <div class="span-12-4">
                <div class="form-box">
                  <label>{{ 'BankTransaction_Details_Form_Field_InstrumentType' | translateWF: 'Instrument type' }}</label>
                  <p-select [formControl]="instrument.type" [options]="instrumentTypeOptions" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full">
                    <ng-template #selectedItem let-option>{{ option.labelKey ? (option.labelKey | translateWF: option.label) : option.label }}</ng-template>
                    <ng-template #item let-option>{{ option.labelKey ? (option.labelKey | translateWF: option.label) : option.label }}</ng-template>
                  </p-select>
                </div>
              </div>

              @if (showWireFields()) {
                <div class="span-12-4">
                  <div class="form-box">
                    <label>{{ 'BankTransaction_Details_Form_Field_EndToEndId' | translateWF: 'End-to-end ID' }} <span class="text-red-500">*</span></label>
                    <input
                      type="text"
                      pInputText
                      [formControl]="instrument.endToEndId"
                      class="w-full"
                      [ngClass]="{ 'ng-invalid ng-dirty': instrument.endToEndId.invalid && instrument.endToEndId.touched }"
                    />
                    <small class="text-surface-500">{{
                      'BankTransaction_EndToEndId_Hint' | translateWF: 'The ID sent with the wire. Put what the statement shows in Statement reference below.'
                    }}</small>
                  </div>
                </div>
                <div class="span-12-4">
                  <div class="form-box">
                    <label>{{ 'BankTransaction_Details_Form_Field_Uetr' | translateWF: 'UETR' }}</label>
                    <input type="text" pInputText [formControl]="instrument.uetr" class="w-full" />
                  </div>
                </div>
                <div class="span-12-4">
                  <div class="form-box">
                    <label>{{ 'BankTransaction_Details_Form_Field_ChargeBearer' | translateWF: 'Charge bearer' }}</label>
                    <input type="text" pInputText [formControl]="instrument.chargeBearer" class="w-full" />
                  </div>
                </div>
              }

              @if (showAchFields()) {
                <div class="span-12-4">
                  <div class="form-box">
                    <label>{{ 'BankTransaction_Details_Form_Field_TraceNumber' | translateWF: 'Trace number' }} <span class="text-red-500">*</span></label>
                    <input
                      type="text"
                      pInputText
                      [formControl]="instrument.traceNumber"
                      class="w-full"
                      [ngClass]="{ 'ng-invalid ng-dirty': instrument.traceNumber.invalid && instrument.traceNumber.touched }"
                    />
                    <small class="text-surface-500">{{
                      'BankTransaction_TraceNumber_Hint' | translateWF: 'The trace sent with the ACH. Put what the statement shows in Statement reference below.'
                    }}</small>
                  </div>
                </div>
              }

              <ng-container *ngTemplateOutlet="statementReferenceField" />
            </div>
          </p-card>
        </p-panel>
      }

      <!-- Money -->
      <p-panel [header]="moneyTitle().key | translateWF: moneyTitle().fallback">
        <p-card>
          <div class="grid-12">
            <div class="span-12-6">
              <div class="form-box">
                <label>{{ amountLabel().key | translateWF: amountLabel().fallback }} <span class="text-red-500">*</span></label>
                <p-inputGroup>
                  <p-inputNumber
                    [formControl]="controls.amount"
                    mode="decimal"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="2"
                    [min]="0"
                    styleClass="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': controls.amount.invalid && controls.amount.touched }"
                  />
                  <p-inputGroupAddon>{{ currencyLabel || '—' }}</p-inputGroupAddon>
                </p-inputGroup>
              </div>
            </div>

            <div class="span-12-6">
              <div class="form-box">
                <label>{{ dateLabel().key | translateWF: dateLabel().fallback }} <span class="text-red-500">*</span></label>
                <p-datepicker
                  [formControl]="controls.date"
                  [showIcon]="true"
                  appendTo="body"
                  styleClass="w-full"
                  [ngClass]="{ 'ng-invalid ng-dirty': controls.date.invalid && controls.date.touched }"
                />
              </div>
            </div>

            @if (!controls.instrument) {
              <ng-container *ngTemplateOutlet="statementReferenceField" />
            }

            @if (controls.memo; as memo) {
              <div class="span-12">
                <div class="form-box">
                  <label>{{ 'BankTransaction_Details_Form_Field_Concept' | translateWF: 'Description / memo' }}</label>
                  <textarea
                    pTextarea
                    [formControl]="memo"
                    rows="3"
                    class="w-full"
                    [placeholder]="'BankTransaction_Concept_Placeholder' | translateWF: 'What this transaction is for'"
                  ></textarea>
                </div>
              </div>
            }
          </div>
        </p-card>
      </p-panel>

      <!-- The other side of the entry, broken down by account. Absent when the document supplies it rather
         than the user picking it — an internal transfer always posts against Cash in Transit, and having no
         control here is what makes splitting one unreachable rather than merely discouraged. -->
      @if (controls.counterAccount) {
        <p-panel>
          <ng-template pTemplate="header">
            <span class="p-panel-title">
              {{ counterAccountLabel }}
              @if (counterAccountRequired) {
                <span class="text-red-500">*</span>
              }
            </span>
          </ng-template>
          @if (editable) {
            <ng-template pTemplate="icons">
              <p-button [label]="'BankTransaction_AddCounterLine' | translateWF: 'Add account'" icon="pi pi-plus" size="small" variant="outlined" (click)="addCounterLine()" />
            </ng-template>
          }
          <p-card>
            <!-- A table, not a stack of form rows: one bank line against several accounts is a list of
               amounts that has to add up, and the column of figures under a Total is what makes a wrong one
               visible. The same shape journal-entry-manual uses for its lines, for the same reason. -->
            <p-table [value]="counterLineRows()" [tableStyle]="{ 'min-width': '40rem' }">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width: 3rem">#</th>
                  <th>{{ 'BankTransaction_CounterLine_Account' | translateWF: 'Account' }}</th>
                  <th>{{ 'BankTransaction_CounterLine_Description' | translateWF: 'What for' }}</th>
                  <th class="text-right" style="width: 12rem">{{ 'BankTransaction_CounterLine_Amount' | translateWF: 'Amount' }}</th>
                  <th style="width: 4rem"></th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                <tr [formGroup]="getCounterGroup(row)">
                  <td>{{ rowIndex + 1 }}</td>

                  <td>
                    <app-financial-account-select
                      [referenceControl]="getCounterGroup(row).controls.reference"
                      [numberControl]="getCounterGroup(row).controls.number"
                      [nameControl]="getCounterGroup(row).controls.name"
                      [required]="counterAccountRequired"
                      [readonly]="!editable"
                      [postingOnly]="true"
                      [postingModule]="PostingModule.Banking"
                      [placeholder]="'BankTransaction_CounterLine_SelectAccount' | translateWF: 'Select account'"
                    />
                  </td>

                  <td>
                    <input
                      pInputText
                      [formControl]="getCounterGroup(row).controls.description"
                      class="w-full"
                      [placeholder]="'BankTransaction_CounterLine_Description_Placeholder' | translateWF: 'Optional'"
                    />
                  </td>

                  <td class="text-right">
                    <p-inputNumber
                      [formControl]="getCounterGroup(row).controls.amount"
                      mode="decimal"
                      [minFractionDigits]="2"
                      [maxFractionDigits]="2"
                      [min]="0"
                      placeholder="0.00"
                      inputStyleClass="text-right w-full"
                      styleClass="w-full"
                    />
                  </td>

                  <td>
                    @if (editable) {
                      <p-button
                        icon="pi pi-trash"
                        severity="danger"
                        [text]="true"
                        [rounded]="true"
                        [pTooltip]="'Remove' | translateWF: 'Remove'"
                        (click)="removeCounterLine(rowIndex)"
                      />
                    }
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="5" class="text-center text-surface-500">
                    {{ 'BankTransaction_NoCounterLines' | translateWF: 'No offsetting account yet. Add one — or several, if this movement was more than one thing.' }}
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="footer">
                <tr>
                  <td colspan="3">{{ 'BankTransaction_CounterLine_Allocated' | translateWF: 'Allocated' }}</td>
                  <td class="text-right tabular-nums">{{ counterTotal() | number: '1.2-2' }} {{ currencyLabel }}</td>
                  <td></td>
                </tr>
              </ng-template>
            </p-table>

            <!-- The rule the API enforces at post, stated here while it can still be fixed in place. Signed,
               because "over by 500" and "500 still to allocate" are different mistakes. -->
            @if (showCounterTotals()) {
              <div class="net-row" [ngClass]="{ 'text-red-500': !counterBalanced() }">
                <span>
                  @if (counterBalanced()) {
                    <span class="pi pi-check-circle mr-2"></span>
                    {{ 'BankTransaction_CounterLine_Matches' | translateWF: 'Matches the amount' }}
                  } @else {
                    <span class="pi pi-exclamation-triangle mr-2"></span>
                    @if (counterRemaining() > 0) {
                      {{ 'BankTransaction_CounterLine_Short' | translateWF: 'Still to allocate' }}
                    } @else {
                      {{ 'BankTransaction_CounterLine_Over' | translateWF: 'Over the amount by' }}
                    }
                  }
                </span>
                @if (!counterBalanced()) {
                  <span class="net-row__amount tabular-nums">
                    {{ (counterRemaining() > 0 ? counterRemaining() : -counterRemaining()) | number: '1.2-2' }} {{ currencyLabel }}
                  </span>
                }
              </div>
            }
          </p-card>
        </p-panel>
      }

      <!-- Charges. Every row is one we bear, so each moves the bank balance. -->
      <p-panel [header]="'BankTransaction_Section_Fees' | translateWF: 'Fees'">
        @if (editable) {
          <ng-template pTemplate="icons">
            <p-button [label]="'BankTransaction_AddFee' | translateWF: 'Add fee'" icon="pi pi-plus" variant="outlined" size="small" (click)="addFee()" />
          </ng-template>
        }
        <p-card>
          @if (feeRows.length === 0) {
            <p class="text-surface-500">{{ 'BankTransaction_NoFees' | translateWF: 'No charges on this movement.' }}</p>
          } @else {
            <div class="col">
              @for (row of feeRows.controls; track $index) {
                <div class="grid-12 items-end">
                  <div class="span-12-5">
                    <div class="form-box">
                      <label>{{ 'BankTransaction_FeeType' | translateWF: 'Fee type' }}</label>
                      <p-select [formControl]="row.controls.type" [options]="feeTypeOptions" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full" />
                    </div>
                  </div>
                  <div class="span-10 md:span-5">
                    <div class="form-box">
                      <label>{{ 'BankTransaction_FeeAmount' | translateWF: 'Amount' }}</label>
                      <p-inputGroup>
                        <p-inputNumber [formControl]="row.controls.amount" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" [min]="0" styleClass="w-full" />
                        <p-inputGroupAddon>{{ currencyLabel || '—' }}</p-inputGroupAddon>
                      </p-inputGroup>
                    </div>
                  </div>
                  <div class="span-2 md:span-2">
                    @if (editable) {
                      <p-button icon="pi pi-trash" severity="danger" variant="text" (click)="removeFee($index)" />
                    }
                  </div>
                </div>
              }
            </div>
          }

          <!-- The number the user checks against their statement. Suppressed on a receipt confirmation, where
           the entered amount is already the net and the gauge below is the number that matters. -->
          @if (showNet()) {
            <div class="net-row">
              <span>{{ netLabel().key | translateWF: netLabel().fallback }}</span>
              <span class="net-row__amount">{{ netAmount() | number: '1.2-2' }} {{ currencyLabel }}</span>
            </div>
          }

          <!-- The zero-difference gauge, on the one mode that has one. -->
          <ng-content select="[movementGauge]" />
        </p-card>
      </p-panel>

      <ng-content select="[movementExtras]" />
    </div>

    <!-- Right column ────────────────────────────────────────────────────────────── -->
    <div class="span-12-12-4 col">
      <!-- Above the preview, for whatever the host wants read first: on a transfer, the request the money
           answers. The preview is a readout, so anything the user still has to fill in outranks it. -->
      <ng-content select="[movementAsideLead]" />

      <!-- The entry as it will post. Live, so a missing charge shows up here before submitting rather than
         as a server error after — and gone once it has posted, where the entry itself is the truth. -->
      @if (showPreview) {
        <p-panel>
          <ng-template pTemplate="header"> <i class="pi pi-book mr-2"></i>{{ previewTitle }} </ng-template>
          <ng-template pTemplate="icons">
            <span class="text-surface-500 text-sm">{{ previewSubtitle }}</span>
          </ng-template>
          <p-card>
            @if (previewLines.length === 0) {
              <p class="text-surface-500">{{ 'BankTransaction_Preview_Empty' | translateWF: 'Pick an account and an amount to see the entry.' }}</p>
            } @else {
              <div class="preview">
                @for (line of previewLines; track $index) {
                  <div class="preview__row">
                    <div class="preview__account">
                      <span>{{ line.accountName }}</span>
                      <small class="text-surface-500">{{ line.accountNumber || ('BankTransaction_Preview_ResolvedOnPost' | translateWF: 'account resolved on post') }}</small>
                    </div>
                    <span class="preview__debit">{{ line.debit ? (line.debit | number: '1.2-2') : '' }}{{ line.debit && line.currency ? ' ' + line.currency : '' }}</span>
                    <span class="preview__credit">{{ line.credit ? (line.credit | number: '1.2-2') : '' }}{{ line.credit && line.currency ? ' ' + line.currency : '' }}</span>
                  </div>
                }
              </div>

              <!-- Warned about, never blocking: the preview cannot resolve the config-driven fee accounts, so
             an unbalanced reading is a prompt to look rather than proof the entry is wrong. -->
              @if (previewMixedCurrency) {
                <!-- No total, because there is no total to state. The two sides of a converted transfer are in
               different money, and the column that does balance is the functional one, which the API
               computes at post from the rate on each line. -->
                <div class="preview__footer">
                  <i class="pi pi-info-circle"></i>
                  <span>
                    {{ 'BankTransaction_Preview_Mixed' | translateWF: 'The two sides are in different currencies — this entry balances in the books, not column by column.' }}
                  </span>
                </div>
              } @else {
                <div class="preview__footer" [class.preview__footer--unbalanced]="!previewBalanced()">
                  <i [class]="previewBalanced() ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'"></i>
                  <span>
                    {{ previewBalanced() ? ('BankTransaction_Balanced' | translateWF: 'Balanced') : ('BankTransaction_Unbalanced' | translateWF: 'Not balanced') }}
                    · {{ previewDebits() | number: '1.2-2' }} {{ currencyLabel }}
                  </span>
                </div>
              }
            }

            <ng-content select="[movementPreviewFooter]" />
          </p-card>
        </p-panel>
      }

      <ng-content select="[movementAside]" />
    </div>
  </div>
</div>
